import * as THREE from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';

export class DevEditor {
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private renderer: THREE.WebGLRenderer;
  private orbitControls: any; // Using any or specific type if imported, typically OrbitControls
  
  private transformControl: TransformControls;
  private raycaster: THREE.Raycaster;
  private pointer: THREE.Vector2;
  private targetGroup: THREE.Group | null = null;
  
  private uiOverlay: HTMLDivElement;
  private boundOnPointerDown: (event: PointerEvent) => void;
  private boundOnKeyDown: (event: KeyboardEvent) => void;

  constructor(scene: THREE.Scene, camera: THREE.Camera, renderer: THREE.WebGLRenderer, orbitControls: any) {
    this.scene = scene;
    this.camera = camera;
    this.renderer = renderer;
    this.orbitControls = orbitControls;

    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();

    // Initialize TransformControls
    this.transformControl = new TransformControls(this.camera, this.renderer.domElement);
    
    // In three r150+, TransformControls itself inherits from Object3D
    if (this.transformControl instanceof THREE.Object3D) {
      this.scene.add(this.transformControl);
    } else if ((this.transformControl as any).getHelper) {
      this.scene.add((this.transformControl as any).getHelper());
    }

    // Disable OrbitControls when dragging the TransformControls gizmo
    this.transformControl.addEventListener('dragging-changed', (event) => {
      this.orbitControls.enabled = !event.value;
    });

    // Create UI Overlay
    this.uiOverlay = document.createElement('div');
    this.uiOverlay.style.position = 'fixed';
    this.uiOverlay.style.top = '10px';
    this.uiOverlay.style.right = '10px';
    this.uiOverlay.style.padding = '15px';
    this.uiOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    this.uiOverlay.style.color = '#fff';
    this.uiOverlay.style.fontFamily = 'monospace';
    this.uiOverlay.style.fontSize = '12px';
    this.uiOverlay.style.pointerEvents = 'none'; // click through
    this.uiOverlay.style.zIndex = '9999';
    this.uiOverlay.style.borderRadius = '5px';
    this.uiOverlay.style.whiteSpace = 'pre';
    this.uiOverlay.innerText = 'DevEditor (editMode=true)\nClick an item to edit.\nShortcuts: [T]ranslate, [R]otate, [S]cale, [Esc]ape';
    document.body.appendChild(this.uiOverlay);

    // Update UI when transform changes
    this.transformControl.addEventListener('change', () => {
      this.updateUI();
    });

    // Event Listeners
    this.boundOnPointerDown = this.onPointerDown.bind(this);
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.renderer.domElement.addEventListener('pointerdown', this.boundOnPointerDown);
    window.addEventListener('keydown', this.boundOnKeyDown);
  }

  public setTargetGroup(group: THREE.Group | null) {
    this.targetGroup = group;
    this.transformControl.detach();
    this.updateUI();
  }

  private onPointerDown(event: PointerEvent) {
    if (!this.targetGroup || !this.targetGroup.visible) return;

    // Only handle primary click
    if (event.button !== 0) return;

    // Calculate pointer position in normalized device coordinates (-1 to +1)
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.pointer, this.camera);

    const intersects = this.raycaster.intersectObjects(this.targetGroup.children, true);

    if (intersects.length > 0) {
      // Find the root object that has the 'equipped-' prefix
      let hitObj: THREE.Object3D | null = intersects[0].object;
      while (hitObj && hitObj !== this.targetGroup) {
        if (hitObj.name.startsWith('equipped-') || hitObj.userData.editable) {
          this.transformControl.attach(hitObj);
          this.updateUI();
          return;
        }
        hitObj = hitObj.parent;
      }
    }
  }

  private onKeyDown(event: KeyboardEvent) {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

    switch (event.key.toLowerCase()) {
      case 't':
        this.transformControl.setMode('translate');
        break;
      case 'r':
        this.transformControl.setMode('rotate');
        break;
      case 's':
        this.transformControl.setMode('scale');
        break;
      case 'escape':
        this.transformControl.detach();
        this.updateUI();
        break;
    }
  }

  private updateUI() {
    const obj = this.transformControl.object;
    if (!obj) {
      this.uiOverlay.innerText = 'DevEditor (editMode=true)\nClick an item to edit.\nShortcuts: [T]ranslate, [R]otate, [S]cale, [Esc]ape';
      return;
    }

    const p = obj.position;
    const r = obj.rotation;
    const s = obj.scale;

    this.uiOverlay.innerText = `[ DevEditor ]
Object: ${obj.name}
Mode: ${this.transformControl.getMode()}

manPosition: [${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)}],
manRotation: [${r.x.toFixed(3)}, ${r.y.toFixed(3)}, ${r.z.toFixed(3)}],
manScale: [${s.x.toFixed(3)}, ${s.y.toFixed(3)}, ${s.z.toFixed(3)}]
`;
  }

  public dispose() {
    this.transformControl.detach();
    if (this.transformControl instanceof THREE.Object3D) {
      this.scene.remove(this.transformControl);
    } else if ((this.transformControl as any).getHelper) {
      this.scene.remove((this.transformControl as any).getHelper());
    }
    this.transformControl.dispose();
    if (this.uiOverlay.parentElement) {
      this.uiOverlay.parentElement.removeChild(this.uiOverlay);
    }
    this.renderer.domElement.removeEventListener('pointerdown', this.boundOnPointerDown);
    window.removeEventListener('keydown', this.boundOnKeyDown);
  }
}
